import { useRef, useState } from 'react';
import { IconUpload, IconX, IconPhoto, IconArrowLeft } from '@tabler/icons-react';
import './ImageUploader.css';

export default function ImageUploader({ image, onUpload, onRemove, label, aspectRatio }) {
  const inputRef = useRef();

  function handleFile(e) {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      alert('Image too large. Please use an image under 5MB.');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      onUpload(reader.result);
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  }

  return (
    <div className="img-uploader" style={{ aspectRatio: aspectRatio || '4/3' }}>
      {image ? (
        <div className="img-uploader__preview">
          <img src={image} alt={label || 'Uploaded'} />
          <button className="img-uploader__remove" onClick={onRemove} title="Remove image">
            <IconX size={12} />
          </button>
        </div>
      ) : (
        <button className="img-uploader__empty" onClick={() => inputRef.current.click()}>
          <IconUpload size={18} />
          <span>{label || 'Upload photo'}</span>
        </button>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        onChange={handleFile}
        style={{ display: 'none' }}
      />
      {image && (
        <button className="img-uploader__change" onClick={() => inputRef.current.click()}>
          <IconPhoto size={12} /> Change
        </button>
      )}
    </div>
  );
}

// Product photo grid. images is a packed array of URLs (no gaps).
//   onAdd(files, replaceIndex)  replaceIndex is null when adding new photos
//   onRemove(index)
//   onReorder(fromIndex, toIndex)
//   uploading: how many photos are uploading right now (shown as placeholders)
export function ImageGrid({ images, onAdd, onRemove, onReorder, maxSlots, uploading }) {
  const slots = maxSlots || 6;
  const list = (images || []).filter(Boolean);
  const busy = uploading || 0;
  const inputRef = useRef();
  const replaceIndex = useRef(null);
  const [dragFrom, setDragFrom] = useState(null);
  const [dragOver, setDragOver] = useState(null);

  function openPicker(index) {
    replaceIndex.current = index;
    inputRef.current.multiple = index === null;
    inputRef.current.click();
  }

  function handleFiles(e) {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (files.length) onAdd(files, replaceIndex.current);
  }

  function endDrag() { setDragFrom(null); setDragOver(null); }

  function dropOn(index, e) {
    e.preventDefault();
    const dropped = Array.from((e.dataTransfer && e.dataTransfer.files) || []);
    if (dragFrom === null && dropped.length) {
      onAdd(dropped, null);
    } else if (dragFrom !== null && index !== null && index !== dragFrom && onReorder) {
      onReorder(dragFrom, Math.min(index, list.length - 1));
    }
    endDrag();
  }

  const emptyCount = Math.max(0, slots - list.length - busy);

  return (
    <div className="img-grid">
      {list.map((img, i) => (
        <div
          className={'img-grid__slot' + (dragOver === i && dragFrom !== null && dragFrom !== i ? ' img-grid__slot--over' : '') + (dragFrom === i ? ' img-grid__slot--dragging' : '')}
          key={img + '#' + i}
          style={{ aspectRatio: '1' }}
          draggable={!!onReorder}
          data-testid={'photo-slot-' + i}
          onDragStart={(e) => { setDragFrom(i); if (e.dataTransfer) { e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', String(i)); } }}
          onDragOver={(e) => { e.preventDefault(); if (dragOver !== i) setDragOver(i); }}
          onDragLeave={() => { if (dragOver === i) setDragOver(null); }}
          onDrop={(e) => dropOn(i, e)}
          onDragEnd={endDrag}
        >
          <div className="img-grid__filled">
            <img src={img} alt={'Photo ' + (i + 1)} draggable={false} />
            <div className="img-grid__overlay">
              {i > 0 && onReorder && (
                <button type="button" onClick={() => onReorder(i, i - 1)} title="Move earlier" aria-label={'Move photo ' + (i + 1) + ' earlier'}><IconArrowLeft size={12} /></button>
              )}
              <button type="button" onClick={() => openPicker(i)} title="Replace" aria-label={'Replace photo ' + (i + 1)}><IconPhoto size={12} /></button>
              <button type="button" onClick={() => onRemove(i)} title="Remove" aria-label={'Remove photo ' + (i + 1)}><IconX size={12} /></button>
            </div>
            {i === 0 && <span className="img-grid__badge">Main</span>}
          </div>
        </div>
      ))}
      {Array.from({ length: Math.min(busy, slots - list.length) }).map((_, k) => (
        <div className="img-grid__slot img-grid__slot--busy" key={'busy-' + k} style={{ aspectRatio: '1' }}>
          <span>Uploading…</span>
        </div>
      ))}
      {Array.from({ length: emptyCount }).map((_, k) => {
        const first = list.length + busy + k === 0;
        return (
          <div
            className="img-grid__slot"
            key={'empty-' + k}
            style={{ aspectRatio: '1' }}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => dropOn(null, e)}
          >
            <button type="button" className="img-grid__empty" onClick={() => openPicker(null)}>
              <IconUpload size={16} />
              <span>{first ? 'Main photo' : 'Add photo'}</span>
            </button>
          </div>
        );
      })}
      <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif,image/avif,image/heic,image/heif" onChange={handleFiles} style={{ display: 'none' }} data-testid="photo-input" />
    </div>
  );
}
